import { useNavigate, useParams } from 'react-router-dom'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { TrainingFormsDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  CreateTrainingFormDocument,
  IsFormsAbstractionEnabledDocument,
  TrainingFormsDocument,
} from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getDefaultOptions } from 'modules/forms/hooks/useForms'
import { RatingElement } from 'modules/forms/lib/elements/RatingElement'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'

import { BasicInfoStep } from './BasicInfoStep'
import { FormTypeStep } from './FormTypeStep'
import { FormWizardData } from './types'

export const NewFormWizard = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams<'id'>()
  const { company, access } = useCurrentSession()
  const [createTrainingForm, { loading }] = useMutation(CreateTrainingFormDocument, {
    extractorFn: (data) => data.formsMutations.createFormsForm,
    awaitRefetchQueries: true,
    refetchQueries: [TrainingFormsDatatableDocument, TrainingFormsDocument],
  })

  const initialData: FormWizardData = {
    anonymous: false,
    title: '',
    description: '',
    trainingFormType: 'satisfaction',
  }
  const { data: featuresData } = useQuery(IsFormsAbstractionEnabledDocument, {
    variables: { companyId: company.id },
  })
  const hasFormsFF = featuresData?.features.featuresConnection.nodes[0]?.enabled ?? false

  const form = useForm<FormWizardData>({
    defaultValues: initialData,
    onSubmit: async (data) => {
      if (!id) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.surveys.create.error.invalid_id'),
        })
        return {
          success: false,
          errors: [
            { type: 'generic_error', message: i18n.t('trainings.surveys.create.error.invalid_id') },
          ],
        }
      }

      const section = new SectionElement(
        '',
        data.trainingFormType === 'effectiveness'
          ? i18n.t('trainings.surveys.create.effectiveness.overall_effectiveness.title')
          : i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.title'),
        '',
        0
      )
      section.lock()
      section.setParam(
        'scoreType',
        data.trainingFormType === 'effectiveness' ? 'courseeffectiveness' : 'coursesatisfaction'
      )
      const rating = new RatingElement(
        '',
        data.trainingFormType === 'effectiveness'
          ? i18n.t('trainings.surveys.create.effectiveness.overall_effectiveness.content')
          : i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.content'),
        '',
        0,
        true
      )
      rating.options = getDefaultOptions('rating')
      rating.lock()
      section.addElement(rating)
      const elements = !hasFormsFF ? [] : [section.toBackend()]
      const result = await createTrainingForm({
        variables: {
          ...data,
          companyId: company.id,
          resourceId: id ? id.toString() : '',
          resourceType: 'Trainings::Training',
          authorId: access.id,
          anonymous: data.trainingFormType === 'effectiveness' ? false : data.anonymous,
          formType: data.trainingFormType,
          questionnaire: hasFormsFF
            ? []
            : [
                {
                  uuid: uuid(),
                  type: 'section',
                  locked: true,
                  metadata: {
                    scoreType:
                      data.trainingFormType === 'effectiveness'
                        ? 'courseeffectiveness'
                        : 'coursesatisfaction',
                  },
                  sectionTitle:
                    // TODO: Handle default questions  on the backend
                    data.trainingFormType === 'effectiveness'
                      ? i18n.t('trainings.surveys.create.effectiveness.overall_effectiveness.title')
                      : i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.title'),
                  questions: [
                    {
                      title:
                        // TODO: Handle default questions  on the backend
                        data.trainingFormType === 'effectiveness'
                          ? i18n.t(
                              'trainings.surveys.create.effectiveness.overall_effectiveness.content'
                            )
                          : i18n.t(
                              'trainings.surveys.create.satisfaction.overall_satisfaction.content'
                            ),
                      answerType: 'rating',
                      mandatory: true,
                      uuid: uuid(),
                      maxChoices: 0,
                      scale: [
                        {
                          text: i18n.t('trainings.surveys.scale.very_low'),
                          value: 1,
                        },
                        {
                          text: i18n.t('trainings.surveys.scale.low'),
                          value: 2,
                        },
                        {
                          text: i18n.t('trainings.surveys.scale.average'),
                          value: 3,
                        },
                        {
                          text: i18n.t('trainings.surveys.scale.high'),
                          value: 4,
                        },
                        {
                          text: i18n.t('trainings.surveys.scale.very_high'),
                          value: 5,
                        },
                      ],
                    },
                  ],
                },
              ],
          elements: elements,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.surveys.create.error.generic_error_message'),
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.surveys.create.success'),
        })
        navigate(
          navigation.trainings.list.details_revamp.trainingSurveyDetail.fullPath({
            id: id,
            formId: handled.data.form?.id || '',
          })
        )
      }
      return handled
    },
  })

  const formHandleSubmit = form.handleSubmit
  const onSubmit = useUnsafeCallback(() => formHandleSubmit(), [formHandleSubmit])

  const surveyTypeStep: WizardStep<FormWizardData> = {
    title: i18n.t('trainings.surveys.create.survey_type'),
    nextText: i18n.t('common.next'),
    render: (props) => <FormTypeStep {...props} />,
    isCompleted: () => true,
  }
  const basicInfoStep: WizardStep<FormWizardData> = {
    title: i18n.t('trainings.surveys.create.basic_information'),
    nextText: i18n.t('common.save'),
    render: (props) => <BasicInfoStep {...props} />,
    isCompleted: () => Boolean(form.values.title) || !loading,
  }
  return (
    <Wizard.Modal
      icon="server-edit"
      title={i18n.t('trainings.surveys.create.new_survey')}
      steps={[surveyTypeStep, basicInfoStep]}
      initialData={initialData}
      onSubmit={onSubmit}
      onDataChanged={form.setValues}
      onClose={() =>
        navigate(navigation.trainings.list.details_revamp.view.surveys.fullPath({ id: Number(id) }))
      }
      size="large"
    />
  )
}
